import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CreateCustomerDialog } from "@/components/customers/CreateCustomerDialog";
import { GatePassOptionCard } from "@/components/sales/GatePassOptionCard";
import { SaleInstallmentPlanner } from "@/components/sales/SaleInstallmentPlanner";
import { MoneyAmountField } from "@/components/sales/MoneyAmountInput";
import { SaleProductPicker } from "@/components/sales/SaleProductPicker";
import {
  ProductDealSummary,
  type ProductDealSummaryData,
} from "@/components/sales/ProductDealSummary";
import {
  PaymentMethodDetailFields,
  PaymentMethodSelect,
  applyPaymentMethodChange,
  emptyPaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import type { ProductDealType, ProductProfitRuleType } from "@/lib/catalog/dealPartners";
import { fetchProductFromApi, type ProductRecord } from "@/lib/catalog/productsStore";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { listCustomers, syncCustomersFromApi, formatCustomerDisplayName } from "@/lib/customers/customersStore";
import { listEmployees, syncEmployeesFromApi } from "@/lib/hr/employeesStore";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import type { SaleProductPick } from "@/lib/sales/saleProductSearchStore";
import { buildInstallmentPlan, installmentsMatchBalance, type SaleInstallmentDraft } from "@/lib/sales/saleInstallments";
import { fetchSaleFromApi } from "@/lib/sales/salesIndexStore";
import { formatDeliveryPayload, nowDeliveryParts } from "@/lib/sales/gatePass";
import { cn } from "@/lib/utils";
import {
  createSale,
  saleErrorMessage,
  type SalePaymentMethod,
  type SaleTokenType,
} from "@/lib/sales/salesStore";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { format } from "date-fns";
import { ArrowLeft, Calculator, CheckCircle2, FileText, Loader2, Package, Plus, Save, Wallet } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

type SalePaymentMode = "full" | "token" | "installment";

function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SectionHeader({
  title,
  subtitle,
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <h3 className="font-display text-sm font-semibold text-foreground">{title}</h3>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function FieldGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-3 border-t border-border/40 pt-2 first:border-t-0 first:pt-0">
      {title ? <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4> : null}
      {children}
    </div>
  );
}

function RequiredLabel({ children }: { children: ReactNode }) {
  return (
    <Label className="text-xs text-muted-foreground">
      {children} <span className="text-destructive">*</span>
    </Label>
  );
}

function dealSummaryFromProduct(p: ProductRecord): ProductDealSummaryData {
  const landed = p.landedCost ?? computeLandedCost(p.purchasePrice, p.purchaseCostLines);
  return {
    dealType: (p.dealType ?? null) as ProductDealType | null,
    profitRuleType: (p.profitRuleType ?? null) as ProductProfitRuleType | null,
    ownerDemandPrice: p.ownerDemandPrice ?? null,
    expectedSalePrice: p.expectedSalePrice ?? null,
    landedCost: landed ?? null,
    salePrice: p.salePrice ?? null,
    dealPartners: p.dealPartners ?? [],
    showroomCharge: p.showroomCharge ?? p.companyOwnerCommission ?? null,
    companyOwnerCommission: p.showroomCharge ?? p.companyOwnerCommission ?? null,
  };
}

const CreateSale = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const [searchParams] = useSearchParams();
  const prefillProductId = searchParams.get("product_id")?.trim() ?? "";

  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "sale_can_create") || can(user, "product_can_view_prices");
  const canEdit = can(user, "sale_can_update") || can(user, "sale_can_create");
  const canSave = isEdit ? canEdit : canCreate;
  const canCreateCustomer = can(user, "customer_can_create");

  const [customerVersion, setCustomerVersion] = useState(0);
  const [employeeVersion, setEmployeeVersion] = useState(0);
  const [refsLoading, setRefsLoading] = useState(true);
  const [prefillLoading, setPrefillLoading] = useState(Boolean(prefillProductId) && !isEdit);
  const [editHydrated, setEditHydrated] = useState(() => !isEdit);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", refresh);
    window.addEventListener("auth_user_updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("auth_user_updated", refresh);
    };
  }, []);

  useEffect(() => {
    void Promise.all([
      syncCustomersFromApi(),
      syncEmployeesFromApi(),
      syncDepartmentsFromApi(),
    ])
      .then(() => {
        setCustomerVersion((v) => v + 1);
        setEmployeeVersion((v) => v + 1);
      })
      .finally(() => setRefsLoading(false));
  }, []);

  useEffect(() => {
    if (isEdit || !prefillProductId) {
      if (!isEdit) setPrefillLoading(false);
      return;
    }
    let cancelled = false;
    setPrefillLoading(true);
    void fetchProductFromApi(prefillProductId)
      .then((p) => {
        if (cancelled) return;
        if (p && (p.inventoryStatus ?? "draft") === "in_stock") {
          const pick: SaleProductPick = {
            id: String(p.id),
            name: p.name,
            salePrice: p.salePrice ?? null,
            inventoryStatus: p.inventoryStatus ?? "in_stock",
            thumbnailUrl: resolveProductThumbnailUrl({ productImages: p.productImages }),
          };
          setSelectedProduct(pick);
          setProductDeal(dealSummaryFromProduct(p));
          if (pick.salePrice != null && Number.isFinite(pick.salePrice) && pick.salePrice > 0) {
            setSaleTotal(String(pick.salePrice));
          }
        } else if (p) {
          setProductDeal(null);
          toast.error("This product is not in stock and cannot be sold.");
        }
      })
      .finally(() => {
        if (!cancelled) setPrefillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prefillProductId, isEdit]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    setEditLoadError(null);
    setEditHydrated(false);
    void fetchSaleFromApi(editId)
      .then((sale) => {
        if (cancelled) return;
        if (!sale) {
          setEditLoadError("Sale not found.");
          return;
        }
        if (sale.product) {
          const productId = String(sale.product.id);
          setSelectedProduct({
            id: productId,
            name: sale.product.name,
            salePrice: sale.total_amount ?? null,
            inventoryStatus: "sold",
            thumbnailUrl: resolveProductThumbnailUrl(sale.product),
          });
          setProductDealLoading(true);
          void fetchProductFromApi(productId)
            .then((p) => {
              if (cancelled) return;
              setProductDeal(p ? dealSummaryFromProduct(p) : null);
            })
            .catch(() => {
              if (!cancelled) setProductDeal(null);
            })
            .finally(() => {
              if (!cancelled) setProductDealLoading(false);
            });
        } else {
          setProductDeal(null);
          setProductDealLoading(false);
        }
        setSaleCustomerId(sale.customer_id ? String(sale.customer_id) : "");
        setSaleEmployeeId(sale.employee_id ? String(sale.employee_id) : "");
        setSaleDate(sale.date?.slice(0, 10) ?? "");
        setSaleTotal(String(sale.total_amount ?? ""));
        setSaleTokenAmount(sale.token_amount != null ? String(sale.token_amount) : "");
        const isInst = Boolean(sale.is_installment);
        const isFull =
          Number(sale.total_amount ?? 0) > 0 &&
          Number(sale.token_amount ?? 0) >= Number(sale.total_amount ?? 0);
        setSalePaymentMode(isInst ? "installment" : isFull ? "full" : "token");
        if (isInst) setInstallmentEnabled(true);
        setSaleTokenType((sale.token_type as SaleTokenType) || "unconditional");
        setSalePayableDate(sale.payable_date?.slice(0, 10) ?? "");
        setSalePaymentMethod((sale.payment_method as SalePaymentMethod) || "");
        setSaleBankName(sale.bank_name ?? "");
        setSaleTransactionRef(sale.transaction_reference_no ?? "");
        setSalePayOrderNo(sale.pay_order_no ?? "");
        setSaleDescription(sale.description ?? "");
        setEditHydrated(true);
      })
      .catch((error) => {
        if (!cancelled) setEditLoadError(saleErrorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [editId]);



  const [selectedProduct, setSelectedProduct] = useState<SaleProductPick | null>(null);
  const [productDeal, setProductDeal] = useState<ProductDealSummaryData | null>(null);
  const [productDealLoading, setProductDealLoading] = useState(false);
  const productDealRequestRef = useRef(0);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [saleEmployeeId, setSaleEmployeeId] = useState("");
  const [saleDate, setSaleDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [saleTotal, setSaleTotal] = useState("");
  const [saleTokenAmount, setSaleTokenAmount] = useState("");
  const [salePaymentMode, setSalePaymentMode] = useState<SalePaymentMode>("token");
  const [saleTokenType, setSaleTokenType] = useState<SaleTokenType>("unconditional");
  const [salePayableDate, setSalePayableDate] = useState("");
  const [salePaymentMethod, setSalePaymentMethod] = useState<SalePaymentMethod | "">("");
  const [saleBankName, setSaleBankName] = useState("");
  const [saleTransactionRef, setSaleTransactionRef] = useState("");
  const [salePayOrderNo, setSalePayOrderNo] = useState("");
  const [saleDescription, setSaleDescription] = useState("");
  const [saleSaving, setSaleSaving] = useState(false);
  const [gatePassEnabled, setGatePassEnabled] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [installmentEnabled, setInstallmentEnabled] = useState(false);
  const [installmentMonths, setInstallmentMonths] = useState(3);
  const [installmentFirstDueDate, setInstallmentFirstDueDate] = useState("");
  const [installmentRows, setInstallmentRows] = useState<SaleInstallmentDraft[]>([]);
  const installmentsManuallyEditedRef = useRef(false);

  const customers = useMemo(() => listCustomers(), [customerVersion]);
  
  const salesDepartmentId = useMemo(() => {
    const depts = listDepartments();
    const salesDept = depts.find((d) => d.companyId === null && d.name === "Sales");
    return salesDept ? String(salesDept.id) : null;
  }, [employeeVersion]);

  const employees = useMemo(() => {
    return listEmployees().filter((e) => {
      const isSales = salesDepartmentId ? String(e.departmentId) === salesDepartmentId : false;
      const isCurrentlySelected = String(e.id) === saleEmployeeId;
      return isSales || isCurrentlySelected;
    });
  }, [employeeVersion, salesDepartmentId, saleEmployeeId]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const loadProductDealSummary = (productId: string | null | undefined) => {
    const id = productId?.trim() ?? "";
    if (!id) {
      productDealRequestRef.current += 1;
      setProductDeal(null);
      setProductDealLoading(false);
      return;
    }
    const requestId = ++productDealRequestRef.current;
    setProductDealLoading(true);
    void fetchProductFromApi(id)
      .then((p) => {
        if (requestId !== productDealRequestRef.current) return;
        setProductDeal(p ? dealSummaryFromProduct(p) : null);
      })
      .catch(() => {
        if (requestId !== productDealRequestRef.current) return;
        setProductDeal(null);
      })
      .finally(() => {
        if (requestId === productDealRequestRef.current) setProductDealLoading(false);
      });
  };

  const handleProductChange = (product: SaleProductPick | null) => {
    setSelectedProduct(product);
    if (product?.salePrice != null && Number.isFinite(product.salePrice) && product.salePrice > 0) {
      setSaleTotal(String(product.salePrice));
    }
    loadProductDealSummary(product?.id);
  };

  const effectiveSaleTotal = useMemo(() => {
    const trimmed = saleTotal.trim();
    if (!trimmed) return 0;
    const v = Number(trimmed);
    if (!Number.isFinite(v) || v < 0) return 0;
    return Math.round(v * 100) / 100;
  }, [saleTotal]);

  const effectiveTokenAmount = useMemo(() => {
    const trimmed = saleTokenAmount.trim();
    if (!trimmed) return 0;
    const v = Number(trimmed);
    if (!Number.isFinite(v) || v < 0) return 0;
    return Math.round(v * 100) / 100;
  }, [saleTokenAmount]);

  const computedBalance = useMemo(() => {
    const price = effectiveSaleTotal;
    const token = effectiveTokenAmount;
    if (!Number.isFinite(price) || price < 0) return 0;
    const tokenVal = Number.isFinite(token) && token >= 0 ? token : 0;
    return Math.max(0, Math.round((price - tokenVal) * 100) / 100);
  }, [effectiveSaleTotal, effectiveTokenAmount]);

  const effectiveBalance = computedBalance;

  useEffect(() => {
    if (salePaymentMode === "full") {
      setSaleTokenAmount(effectiveSaleTotal > 0 ? String(effectiveSaleTotal) : "");
      setInstallmentEnabled(false);
      setSalePayableDate("");
    } else if (salePaymentMode === "installment") {
      setInstallmentEnabled(true);
    } else {
      setInstallmentEnabled(false);
    }
  }, [salePaymentMode, effectiveSaleTotal]);

  const handlePaymentModeChange = (mode: SalePaymentMode) => {
    setSalePaymentMode(mode);
    if (mode === "full") {
      setSaleTokenAmount(effectiveSaleTotal > 0 ? String(effectiveSaleTotal) : "");
      setInstallmentEnabled(false);
      return;
    }
    if (mode === "installment") {
      setInstallmentEnabled(true);
      if (effectiveTokenAmount >= effectiveSaleTotal) {
        setSaleTokenAmount("");
      }
      return;
    }
    setInstallmentEnabled(false);
    if (effectiveTokenAmount >= effectiveSaleTotal) {
      setSaleTokenAmount("");
    }
  };

  const rebuildInstallmentPlan = (resetManual = true) => {
    if (!installmentEnabled || effectiveBalance <= 0 || !installmentFirstDueDate.trim()) {
      setInstallmentRows([]);
      return;
    }
    setInstallmentRows(buildInstallmentPlan(effectiveBalance, installmentMonths, installmentFirstDueDate));
    if (resetManual) {
      installmentsManuallyEditedRef.current = false;
    }
  };

  useEffect(() => {
    if (!installmentEnabled) {
      setInstallmentRows([]);
      installmentsManuallyEditedRef.current = false;
      return;
    }
    rebuildInstallmentPlan(true);
  }, [installmentEnabled, installmentMonths, installmentFirstDueDate]);

  useEffect(() => {
    if (!installmentEnabled || installmentsManuallyEditedRef.current) return;
    rebuildInstallmentPlan(false);
  }, [effectiveBalance]);

  useEffect(() => {
    if (effectiveBalance <= 0 && installmentEnabled) {
      setInstallmentEnabled(false);
    }
  }, [effectiveBalance, installmentEnabled]);

  const handleInstallmentAmountChange = (installmentNo: number, amount: number) => {
    installmentsManuallyEditedRef.current = true;
    setInstallmentRows((rows) =>
      rows.map((row) =>
        row.installment_no === installmentNo
          ? { ...row, amount: Math.round(Math.max(0, amount) * 100) / 100 }
          : row,
      ),
    );
  };

  const handleInstallmentMonthsChange = (count: number) => {
    installmentsManuallyEditedRef.current = false;
    setInstallmentMonths(count);
  };

  const handlePaymentMethodChange = (method: SalePaymentMethod | "") => {
    const next = applyPaymentMethodChange(
      emptyPaymentMethodDraft({
        paymentMethod: salePaymentMethod,
        bankName: saleBankName,
        transactionReferenceNo: saleTransactionRef,
        payOrderNo: salePayOrderNo,
      }),
      method,
    );
    setSalePaymentMethod(next.paymentMethod);
    setSaleBankName(next.bankName);
    setSaleTransactionRef(next.transactionReferenceNo);
    setSalePayOrderNo(next.payOrderNo);
  };

  const handleCancel = () => navigate("/product-sales");

  const recordSale = async () => {
    if (saleSaving) return;
    const productIdNum = Number(selectedProduct?.id);
    const customerIdNum = Number(saleCustomerId);
    const employeeIdNum = Number(saleEmployeeId);
    const total = effectiveSaleTotal;
    const tokenAmount = effectiveTokenAmount;

    if (!isEdit && (!Number.isFinite(productIdNum) || productIdNum <= 0)) {
      toast.error("Select a product.");
      return;
    }
    if (!Number.isFinite(customerIdNum) || customerIdNum <= 0) {
      toast.error("Select a customer.");
      return;
    }
    if (!Number.isFinite(total) || total <= 0) {
      toast.error("Enter a valid sale price.");
      return;
    }
    if (!Number.isFinite(tokenAmount) || tokenAmount < 0) {
      toast.error("Enter a valid token amount.");
      return;
    }
    if (tokenAmount > total) {
      toast.error("Token amount cannot exceed sale price.");
      return;
    }
    if (salePaymentMode !== "full" && tokenAmount > 0 && !saleTokenType) {
      toast.error("Select token type (conditional or un-conditional).");
      return;
    }
    const isInstallmentSale = salePaymentMode === "installment" || installmentEnabled;
    if (!isEdit && isInstallmentSale) {
      if (effectiveBalance <= 0) {
        toast.error("Installment plan requires a balance amount greater than zero.");
        return;
      }
      if (effectiveBalance > total) {
        toast.error("Balance amount cannot exceed sale price.");
        return;
      }
      if (!installmentFirstDueDate.trim()) {
        toast.error("Select the first installment due date.");
        return;
      }
      if (installmentRows.length === 0) {
        toast.error("Configure a valid installment schedule.");
        return;
      }
      if (installmentRows.some((row) => row.amount <= 0)) {
        toast.error("Each installment amount must be greater than zero.");
        return;
      }
      if (!installmentsMatchBalance(installmentRows, effectiveBalance, 0)) {
        toast.error(`Installment total must match balance amount (${money(effectiveBalance)}).`);
        return;
      }
    }

    let deliveryDate: string | null = null;
    if (salePaymentMode === "full" && gatePassEnabled) {
      deliveryDate = formatDeliveryPayload(gatePassDate, gatePassTime);
      if (!deliveryDate) {
        toast.error("Delivery date and time are required for gate pass.");
        return;
      }
    }

    setSaleSaving(true);
    try {
      const payload = {
        customer_id: customerIdNum,
        employee_id: Number.isFinite(employeeIdNum) && employeeIdNum > 0 ? employeeIdNum : null,
        date: saleDate.trim() || null,
        total_amount: total,
        token_amount: tokenAmount,
        token_type: salePaymentMode !== "full" && tokenAmount > 0 ? saleTokenType : null,
        balance_amount: effectiveBalance,
        payable_date: salePayableDate.trim() || null,
        delivery_date: deliveryDate,
        payment_method: salePaymentMethod || null,
        bank_name: saleBankName.trim() || null,
        transaction_reference_no: saleTransactionRef.trim() || null,
        pay_order_no: salePayOrderNo.trim() || null,
        description: saleDescription.trim() || null,
      };

      if (isEdit && editId) {
        toast.error("This sale record cannot be edited.");
        return;
      }

      const { message } = await createSale({
        product_id: productIdNum,
        ...payload,
        is_installment: isInstallmentSale,
        installment_count: isInstallmentSale ? installmentMonths : null,
        installment_first_due_date: isInstallmentSale ? installmentFirstDueDate.trim() || null : null,
        installments: isInstallmentSale ? installmentRows : undefined,
      });
      toast.success(message);
      navigate("/product-sales");
    } catch (error) {
      toast.error(saleErrorMessage(error));
    } finally {
      setSaleSaving(false);
    }
  };

  const formBlocked = refsLoading || prefillLoading || (isEdit && !editHydrated);
  const pageTitle = isEdit ? "Edit sale" : "Record sale";
  const pageSubtitle = isEdit ? "Update sale details and payment information" : "Create a new product sale and invoice";

  if (!canSave) {
    return (
      <PermissionDenied
        title={isEdit ? "Cannot edit sale" : "Cannot record sale"}
        message={
          isEdit
            ? "You don’t have permission to edit sales. Ask an administrator to grant sale_can_update."
            : "You don’t have permission to record sales. Ask an administrator to grant sale_can_create."
        }
      />
    );
  }

  if (editLoadError) {
    return (
      <Layout title="Edit sale">
        <div className="glass-card p-8 space-y-4">
          <p className="text-sm text-muted-foreground">{editLoadError}</p>
          <Button type="button" variant="glass" onClick={handleCancel}>
            Back to sales
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={pageTitle}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={handleCancel}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-foreground">{pageTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{pageSubtitle}</p>
            </div>
          </div>
          <Button type="button" variant="glass" onClick={handleCancel} disabled={saleSaving}>
            Cancel
          </Button>
        </div>

        {formBlocked ? (
          <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
        <div className="space-y-6">
            <div className="glass-card p-6">
              <SectionHeader
                title="Sale information"
                subtitle="Product, customer, salesperson, and sale date"
                icon={Package}
              />
              <div className="space-y-6">
                <FieldGroup>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="space-y-2 md:col-span-3">
                      <RequiredLabel>Product</RequiredLabel>
                      <div className="min-w-0 space-y-3">
                        {isEdit ? (
                          <Input
                            className="glass-input h-10 bg-muted/30"
                            readOnly
                            value={selectedProduct?.name ?? "—"}
                          />
                        ) : prefillLoading ? (
                          <div className="flex h-10 items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading product…
                          </div>
                        ) : (
                          <SaleProductPicker
                            value={selectedProduct}
                            onChange={handleProductChange}
                            disabled={saleSaving}
                          />
                        )}
                        {selectedProduct || productDealLoading ? (
                          <ProductDealSummary
                            data={productDealLoading ? productDeal : (productDeal ?? {})}
                            loading={productDealLoading}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <RequiredLabel>Customer</RequiredLabel>
                        {canCreateCustomer && !isEdit ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-7 shrink-0 text-primary hover:text-primary"
                            aria-label="Create customer"
                            title="Create customer"
                            onClick={() => setCreateCustomerOpen(true)}
                            disabled={saleSaving || refsLoading}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                      <Select
                        value={saleCustomerId || "__none__"}
                        onValueChange={(v) => setSaleCustomerId(v === "__none__" ? "" : v)}
                        disabled={refsLoading || saleSaving}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder={refsLoading ? "Loading…" : "Select customer"} />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="__none__">Select…</SelectItem>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {formatCustomerDisplayName(c)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Salesperson</Label>
                      <Select
                        value={saleEmployeeId || "__none__"}
                        onValueChange={(v) => setSaleEmployeeId(v === "__none__" ? "" : v)}
                        disabled={refsLoading || saleSaving}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder={refsLoading ? "Loading…" : "Optional"} />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="__none__">None (optional)</SelectItem>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {e.fullName || `${e.firstName} ${e.lastName}`.trim() || e.employeeCode || `Employee #${e.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </FieldGroup>
              </div>
            </div>

            <div className="glass-card p-6 relative z-20">
              <SectionHeader
                title="Sale payment"
                subtitle="Select payment type and enter payment details"
                icon={Wallet}
              />
              <div className="space-y-5">
                <div className="space-y-2 max-w-xs">
                  <Label className="text-xs text-muted-foreground">Payment type</Label>
                  <Select
                    value={salePaymentMode}
                    onValueChange={(v) => handlePaymentModeChange(v as SalePaymentMode)}
                    disabled={saleSaving}
                  >
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue placeholder="Select payment type" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="full">Full Payment (100% Upfront)</SelectItem>
                      <SelectItem value="token">Token / Advance Payment</SelectItem>
                      <SelectItem value="installment">Installment Sale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <FieldGroup
                  title={
                    salePaymentMode === "full"
                      ? "Full payment details"
                      : salePaymentMode === "installment"
                      ? "Down payment & installment details"
                      : "Token payment details"
                  }
                >
                  <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    <MoneyAmountField
                      label="Sale price"
                      required
                      id="sale-price"
                      value={effectiveSaleTotal}
                      disabled={saleSaving}
                      onChange={(amount) => setSaleTotal(amount > 0 ? String(amount) : "")}
                    />

                    {salePaymentMode !== "full" ? (
                      <MoneyAmountField
                        label={salePaymentMode === "installment" ? "Token / Down payment" : "Token amount"}
                        id="token-amount"
                        value={effectiveTokenAmount}
                        disabled={saleSaving}
                        onChange={(amount) => setSaleTokenAmount(amount > 0 ? String(amount) : "")}
                      />
                    ) : null}

                    <PaymentMethodSelect
                      value={salePaymentMethod}
                      onChange={handlePaymentMethodChange}
                      disabled={saleSaving}
                      labelClassName="block text-xs leading-4 text-muted-foreground whitespace-nowrap"
                      triggerClassName="glass-input h-10"
                      className="space-y-2"
                    />

                    <div className="space-y-2">
                      <Label className="block whitespace-nowrap text-xs leading-4 text-muted-foreground">
                        Payment date
                      </Label>
                      <DatePartsPicker
                        value={saleDate}
                        onChange={setSaleDate}
                        fromYear={2000}
                        toYear={currentYear}
                        maxDate={today}
                        placeholder="Select payment date"
                        disabled={saleSaving}
                      />
                    </div>

                    {salePaymentMode !== "full" ? (
                      <>
                        <div className="space-y-2">
                          <Label className="block whitespace-nowrap text-xs leading-4 text-muted-foreground">
                            Token type
                          </Label>
                          <Select
                            value={saleTokenType}
                            onValueChange={(v) => setSaleTokenType(v as SaleTokenType)}
                            disabled={saleSaving}
                          >
                            <SelectTrigger className="glass-input h-10">
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent position="popper">
                              <SelectItem value="conditional">Conditional</SelectItem>
                              <SelectItem value="unconditional">Un-Conditional</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="block whitespace-nowrap text-xs leading-4 text-muted-foreground">
                            Due Date
                          </Label>
                          <DatePartsPicker
                            value={salePayableDate}
                            onChange={setSalePayableDate}
                            fromYear={currentYear}
                            toYear={currentYear + 10}
                            placeholder="Select due date"
                            disabled={saleSaving}
                          />
                        </div>
                      </>
                    ) : null}
                  </div>

                  <PaymentMethodDetailFields
                    method={salePaymentMethod}
                    bankName={saleBankName}
                    transactionReferenceNo={saleTransactionRef}
                    payOrderNo={salePayOrderNo}
                    onBankNameChange={setSaleBankName}
                    onTransactionReferenceChange={setSaleTransactionRef}
                    onPayOrderNoChange={setSalePayOrderNo}
                    disabled={saleSaving}
                    className="mt-4"
                  />

                  {salePaymentMode !== "full" ? (
                    <div className="mt-4 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/[0.04] to-amber-500/10 p-4 shadow-sm backdrop-blur-sm">
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-amber-500/30 bg-amber-500/20 text-amber-600 dark:text-amber-400">
                            <Calculator className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                              Remaining Balance
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              Sale price ({getActiveCompanyCurrency().symbol} {money(effectiveSaleTotal)}) − Token amount ({getActiveCompanyCurrency().symbol} {money(effectiveTokenAmount)})
                            </p>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2 text-left sm:text-right">
                          <span className="font-display text-xl font-bold tabular-nums text-foreground">
                            {getActiveCompanyCurrency().symbol} {money(effectiveBalance)}
                          </span>
                          {pkrAmountShortLabel(effectiveBalance) ? (
                            <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
                              {pkrAmountShortLabel(effectiveBalance)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-emerald-500/[0.04] to-emerald-500/10 p-4 shadow-sm backdrop-blur-sm">
                      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                              Full Amount Received
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">100% upfront payment received on sale date</p>
                          </div>
                        </div>
                        <div className="flex items-baseline gap-2 text-left sm:text-right">
                          <span className="font-display text-xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                            Rs. {money(effectiveSaleTotal)}
                          </span>
                          {pkrAmountShortLabel(effectiveSaleTotal) ? (
                            <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                              {pkrAmountShortLabel(effectiveSaleTotal)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}

                  {salePaymentMode === "full" ? (
                    <div className="mt-4">
                      <GatePassOptionCard
                        enabled={gatePassEnabled}
                        onEnabledChange={setGatePassEnabled}
                        date={gatePassDate}
                        time={gatePassTime}
                        onDateChange={setGatePassDate}
                        onTimeChange={setGatePassTime}
                        disabled={saleSaving}
                      />
                    </div>
                  ) : null}
                </FieldGroup>

                {!isEdit && salePaymentMode === "installment" ? (
                  <FieldGroup title="Installments">
                    <SaleInstallmentPlanner
                      enabled={true}
                      hideToggle={true}
                      onEnabledChange={setInstallmentEnabled}
                      balanceAmount={effectiveBalance}
                      saleDate={saleDate}
                      payableDate={salePayableDate}
                      monthCount={installmentMonths}
                      onMonthCountChange={handleInstallmentMonthsChange}
                      firstDueDate={installmentFirstDueDate}
                      onFirstDueDateChange={setInstallmentFirstDueDate}
                      installments={installmentRows}
                      onInstallmentAmountChange={handleInstallmentAmountChange}
                      disabled={saleSaving}
                    />
                  </FieldGroup>
                ) : null}
              </div>
            </div>

            <div className="glass-card p-6">
              <SectionHeader title="Notes" subtitle="Optional description for this sale" icon={FileText} />
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea
                  className="glass-input min-h-[100px] resize-y"
                  value={saleDescription}
                  onChange={(e) => setSaleDescription(e.target.value)}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-border/40 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-end">
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2 px-8 sm:w-auto"
                onClick={() => void recordSale()}
              >
                {saleSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEdit ? "Save changes" : "Save sale"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <CreateCustomerDialog
        open={createCustomerOpen}
        onOpenChange={setCreateCustomerOpen}
        mode="full"
        title="Add customer"
        onCreated={async (record) => {
          await syncCustomersFromApi();
          setCustomerVersion((v) => v + 1);
          setSaleCustomerId(String(record.id));
        }}
      />
    </Layout>
  );
};

export default CreateSale;
